/**
 * Segmented Control (Epic C-2 — Kwalificaties)
 *
 * Herbruikbare `role="tablist"`-toggle — geen shadcn/Radix (dit project
 * heeft geen `@radix-ui/*`/`cmdk`-dependency), native button-groep i.p.v.
 * een library-component. Gebouwd tijdens Story C-2.1 (voor de
 * "Lijsten beheren"-productcategorie/doelmarkt-toggle) en hergebruikt door
 * Story C-2.2 (de "Per medewerker"/"Wie kan wat"-weergavekeuze) — één
 * generieke component i.p.v. twee bijna-identieke tablist-implementaties.
 */
import type { KeyboardEvent } from 'react';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}

export function SegmentedControl<T extends string>({ options, value, onChange, ariaLabel }: SegmentedControlProps<T>) {
  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + delta + options.length) % options.length;
    const nextOption = options[nextIndex]!;
    onChange(nextOption.value);
  }

  return (
    <div role="tablist" aria-label={ariaLabel} className="inline-flex gap-1 rounded-md border border-[#E2E8F0] bg-white p-1">
      {options.map((option, index) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            data-testid={`segmented-tab-${option.value}`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
              isSelected ? 'bg-[#2F5A7A] text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
